import { ReactNode, useEffect, useState } from "react";
import QRCode from "react-qr-code";

import { useAppSelector } from "store";

import certificateQrEmptyImg from "image/certificate-qr-empty.png";
import certificateImg from "image/сertificate.png";

import { LoadingSpinner } from "elements";
import { certificateDataSelector } from "features";
import { formatPrice } from "utils";

import "./CertificatePage.scss";

interface ICertificateInfoBlockProps {
  label: string;
  text: ReactNode;
}

const CertificateInfoBlock = ({ label, text }: ICertificateInfoBlockProps) => (
  <div className="certificate-info-block">
    <span className="certificate-info-block__label">{label}</span>
    <span className="certificate-info-block__text">{text}</span>
  </div>
);

const SUPPORT_PHONE = "8-800-200-75-75";
const SUPPORT_PHONE_HREF = "tel:+78002007575";

const CertificatePage = () => {
  const {
    result,
    fetching,
    success,
    message: requestMessage,
  } = useAppSelector(certificateDataSelector) ?? {};

  const remoteImageUrl = result?.URLКартинки;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [remoteImageUrl]);

  const balance = result?.БалансСертификата;
  const certificateNumber = result?.НомерСертификата;
  const textColor = result?.ЦветТекста;
  const senderName = result?.Отправитель ?? "";
  const message = result?.Текст ?? "";

  // pristine store (до pending) тоже показываем как loading, чтобы не мигал empty state
  const isLoading = fetching || (!result && !success && requestMessage == null);

  if (isLoading) {
    return (
      <div className="certificate-page">
        <h2 className="certificate-page__title">Ваш сертификат</h2>
        <div className="certificate-page__loader">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const isEmptyState = !success || !result;

  if (isEmptyState) {
    return (
      <div className="certificate-page">
        <h2 className="certificate-page__title">Сертификат не найден</h2>
        <div className="certificate-page__image-wrapper">
          <img
            className="certificate-page__image"
            src={certificateImg}
            alt="Сертификат"
          />
          <div className="certificate-page__qr" aria-label="QR-код недоступен">
            <img
              className="certificate-page__qr-placeholder"
              src={certificateQrEmptyImg}
              alt=""
            />
          </div>
        </div>
        <div className="certificate-page__blocks">
          <p className="certificate-info-block__text">
            Убедитесь, что открыли правильную ссылку
          </p>
          <CertificateInfoBlock
            label="За дополнительной информацией обращайтесь по телефону"
            text={
              <a
                className="certificate-info-block__phone"
                href={SUPPORT_PHONE_HREF}
              >
                {SUPPORT_PHONE}
              </a>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="certificate-page">
      <h2 className="certificate-page__title">Ваш сертификат</h2>
      <div className="certificate-page__image-wrapper">
        <img
          className="certificate-page__image"
          src={!failed && remoteImageUrl ? remoteImageUrl : certificateImg}
          alt="Сертификат"
          onError={() => setFailed(true)}
        />
        {balance != null && (
          <div className="certificate-page__balance" style={{ color: textColor }}>
            <span className="certificate-page__balance-value">
              {formatPrice(balance)} ₽
            </span>
          </div>
        )}
        {certificateNumber && (
          <div className="certificate-page__qr" aria-label="QR-код сертификата">
            <QRCode
              value={certificateNumber}
              style={{ width: "100%", height: "100%" }}
              viewBox="0 0 256 256"
            />
          </div>
        )}
      </div>
      <div className="certificate-page__blocks">
        <CertificateInfoBlock label="Имя отправителя" text={senderName} />
        <CertificateInfoBlock label="Сообщение отправителя" text={message} />
      </div>
    </div>
  );
};

export default CertificatePage;
